import { Features } from 'generated/featureDefinitions'
import {
  ClockinCreateEmployeePulseDocument,
  EmployeePulseQuestionDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { NewEmployeePulseModal } from 'modules/engagement/components/NewEmployeePulseModal'

const MIN_MINUTE_PULSE = 0.6

interface Props {
  onClose: () => void
  estimatedTime: number
  workedTime: number
}

const EmployeePulseQuestion = ({ onClose, estimatedTime, workedTime }: Props) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const { data: employeePulseQuestionData, loading } = useQuery(EmployeePulseQuestionDocument, {
    variables: {
      companyId: companyId,
    },
  })

  const { featureAvailability: questionsFeatureAvailabilityEnabled } = useAttendanceFeatures(
    Features.engagement_employee_pulse_questions
  )

  const multipleTimesEnabled = employeePulseQuestionData?.features.featuresConnection.edges.find(
    (feature) => feature.node.name === 'engagement_employee_pulse_question_multiple_times'
  )?.node.enabled

  const questionsFFEnabled = employeePulseQuestionData?.features.featuresConnection.edges.find(
    (feature) => feature.node.name === 'engagement_employee_pulse_questions'
  )?.node.enabled

  const questionsEnabled = questionsFFEnabled || questionsFeatureAvailabilityEnabled

  const [createEmployeePulse] = useMutation(ClockinCreateEmployeePulseDocument, {
    extractorFn: (data) => data?.engagementMutations?.createEngagementEmployeePulse,
  })

  const minPulseTime = Math.round(estimatedTime * MIN_MINUTE_PULSE)
  const hasEstimatedTime = estimatedTime > 0 // We want to avoid days off or holidays
  const hasReachedMinimumWorkedHours = hasEstimatedTime && workedTime >= minPulseTime

  const currentPulse =
    employeePulseQuestionData?.engagement.employeeCurrentPulsesConnection.edges[0]?.node

  let showPrompt = (!loading && currentPulse?.showPrompt && hasReachedMinimumWorkedHours) ?? false
  if (multipleTimesEnabled && questionsEnabled) showPrompt = true

  return (
    showPrompt && (
      <NewEmployeePulseModal
        source="clock_out"
        onClose={() => {
          onClose()
        }}
        onSubmit={(variables) => {
          return createEmployeePulse({
            variables,

            onCompleted(data) {
              const pulse = data?.engagementMutations.createEngagementEmployeePulse.employeePulse

              if (pulse) {
                track(trackingEvents.engagement.employeePulseResponseSubmitted, {
                  submission_method: 'clock_out_survey',
                  pulse_value: pulse.value,
                  comment_provided: !!pulse.comment,
                })
              }
            },
            refetchQueries: [EmployeePulseQuestionDocument],
            awaitRefetchQueries: true,
          })
        }}
      />
    )
  )
}

export default EmployeePulseQuestion
